/**
 * TextareaField component.
 *
 * Multi-line text input with label, required indicator,
 * and inline validation error display.
 */

import type { FormFieldDefinition } from "../../types";

export interface TextareaFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class TextareaField {
  private readonly element: HTMLElement;
  private readonly textareaEl: HTMLTextAreaElement;
  private readonly errorEl: HTMLElement;

  constructor(private readonly options: TextareaFieldOptions) {
    const { field } = options;

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = field.label;
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // Textarea
    this.textareaEl = document.createElement("textarea");
    this.textareaEl.id = `hana-field-${field.name}`;
    this.textareaEl.className = "hana-form-field__textarea";
    this.textareaEl.name = field.name;
    this.textareaEl.rows = 3;
    this.textareaEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.textareaEl.setAttribute("aria-required", "true");
    }
    if (field.placeholder) {
      this.textareaEl.placeholder = field.placeholder;
    }
    if (field.value) {
      this.textareaEl.value = field.value;
    }

    this.textareaEl.addEventListener("input", () => {
      this.clearError();
      this.options.onChange(field.name, this.textareaEl.value);
    });

    this.element.appendChild(this.textareaEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.textareaEl.value.trim();
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.textareaEl.classList.add("hana-form-field__textarea--error");
    this.textareaEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.textareaEl.classList.remove("hana-form-field__textarea--error");
    this.textareaEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.textareaEl.disabled = disabled;
  }

  focus(): void {
    this.textareaEl.focus();
  }
}
